/**
 * Messages the service worker posts to page clients.
 *
 * Published as a shared constant so the worker (sender, src/sw.ts) and the React
 * hook (receiver, usePushRegistration) agree on the vocabulary rather than
 * duplicating a string literal across the worker/page boundary.
 */
export const PUSH_SUBSCRIPTION_CHANGED = "push-subscription-changed" as const;

/**
 * Sent when the browser rotated this device's push subscription
 * (`pushsubscriptionchange`). The worker has already re-subscribed; the page
 * must re-register the live subscription with the backend, which is the only
 * context holding an auth token for the call.
 */
export interface PushSubscriptionChangedMessage {
    readonly type: typeof PUSH_SUBSCRIPTION_CHANGED;
}

export const NAVIGATE_FROM_NOTIFICATION = "navigate-from-notification" as const;

/**
 * Sent when the user clicks a Web Push toast and an open page is available to
 * receive the navigation. The worker focuses the tab and delegates the route
 * change to the page rather than calling WindowClient.navigate(): navigate()
 * rejects on clients this worker doesn't control (common right after an
 * autoUpdate activation) and forces a full document reload. A client-side
 * navigation keeps the SPA router in charge and works regardless of control
 * state. Received by useNotificationNavigation.
 */
export interface NotificationNavigateMessage {
    readonly type: typeof NAVIGATE_FROM_NOTIFICATION;
    /**
     * Basename-relative in-app router path (leading slash), e.g.
     * `/workflows/{id}?entityType=…&entityId=…&item=…`. React Router resolves
     * it against the router basename, so it must not include the `/einstein`
     * deployment prefix.
     */
    readonly path: string;
}

/**
 * Sent by the page to the worker with one server observation of the owed-work
 * count (the taskbar badge's single writer is the worker; see
 * app-badge/badgeWriter.ts). `asOfMs` is the server read instant, so the worker
 * can order it against pushes that arrive out of order.
 */
export const BADGE_OBSERVED = "badge-observed" as const;

export interface BadgeObservedMessage {
    readonly type: typeof BADGE_OBSERVED;
    readonly count: number;
    readonly asOfMs: number;
}

/**
 * Sent by the worker to open pages when a work-item push arrives, so a page
 * that is open but has missed its realtime event re-reads the count and
 * publishes a fresh observation. A hint, not a value: the page's own read is
 * the authority.
 */
export const BADGE_REFRESH = "badge-refresh" as const;

export interface BadgeRefreshMessage {
    readonly type: typeof BADGE_REFRESH;
}

/**
 * Narrows an untrusted `message` payload to a well-formed observation, or
 * `undefined`. Anything from a page is validated before it reaches the fence.
 */
export function readBadgeObserved(data: unknown): BadgeObservedMessage | undefined {
    if (typeof data !== "object" || data === null) {
        return undefined;
    }
    const candidate = data as Partial<Record<"type" | "count" | "asOfMs", unknown>>;
    if (candidate.type !== BADGE_OBSERVED) {
        return undefined;
    }
    const { count, asOfMs } = candidate;
    if (typeof count !== "number" || !Number.isInteger(count) || count < 0) {
        return undefined;
    }
    if (typeof asOfMs !== "number" || !Number.isFinite(asOfMs)) {
        return undefined;
    }
    return { type: BADGE_OBSERVED, count, asOfMs };
}

/** True when a `message` payload is the worker's badge-refresh hint. */
export function isBadgeRefresh(data: unknown): boolean {
    return typeof data === "object" && data !== null && (data as { type?: unknown }).type === BADGE_REFRESH;
}
